/**
 * Seed data for the in-memory mock transport.
 *
 * This exists so a `vite build` with no API base configured still yields a fully
 * clickable, on-brand console — the whole operator surface can be demoed offline. The
 * shapes here are the app-local DTOs and contract types, so if either changes, the
 * seed stops compiling.
 */
import type {
  AdminAssetRail,
  AdminBank,
  AdminBanner,
  AdminGiftcardBrand,
  AdminKycQueueItem,
  AdminLegalDocument,
  AdminMe,
  AdminRateRow,
  AdminRedemptionDetail,
  AssetNetwork,
  Attachment,
  BrandAsset,
  Flag,
  Message,
  ProviderConfig,
  ProviderService,
  ProviderWebhook,
  ReconciliationRow,
  SenderType,
  SplashAsset,
  SupportSettings,
  TierLimit,
} from '../../contracts/types.js';
/*
 * The provisional shapes. The seeds are typed against the *widened* rows on purpose, because
 * a demo backend that could not express a full phone number, a profile photo, a vendor cost
 * or a bill service would leave four of this console's surfaces untestable offline — which is
 * exactly the half of the work order that has no contract behind it yet.
 *
 * Where a field is deliberately left off a seed row, that is a demo of the *absent* case: the
 * console has to degrade to a stated "not available yet" on a server that has not shipped the
 * change, and a seed where every row is complete would never show that path to anybody.
 */
import type {
  AdminKycDetail,
  AdminUserDetail,
  AdminThreadRow,
  AdminTxRow,
  BillService,
  KycDocumentSummary,
} from '../../contracts/types.js';
import {
  DEMO_AVATAR_AMBER,
  DEMO_AVATAR_GREEN,
  DEMO_AVATAR_VIOLET,
  DEMO_ICON_BOLT,
  DEMO_ICON_TV,
  DEMO_KYC_PDF,
  DEMO_KYC_SCAN,
} from './demo-media.js';

export interface MockAdmin extends AdminMe {
  readonly password: string;
}

const iso = (daysAgo: number, hour = 9): string =>
  new Date(Date.UTC(2026, 6, 30 - daysAgo, hour, 15, 0)).toISOString();

/**
 * A timestamp in the future, relative to when the demo is actually opened.
 *
 * The seeds above are pinned to a fixed date so the demo reads the same on every reload, but a
 * lockout expiry is only meaningful ahead of *now*: pinned to July 2026 it would render as a
 * lockout that lapsed months ago, which is the one thing `lockedUntil` cannot be while the
 * status still says locked.
 */
const isoAhead = (hoursFromNow: number): string =>
  new Date(Date.now() + hoursFromNow * 60 * 60 * 1000).toISOString();

// Every demo admin shares one password, shown as a hint on the login screen.
export const DEMO_PASSWORD = 'genex-admin';

export const MOCK_ADMINS: readonly MockAdmin[] = [
  {
    id: 'a1000000-0000-4000-8000-000000000001',
    email: 'superadmin@genex.pay',
    name: 'Ada Superadmin',
    role: 'superadmin',
    lastLoginAt: iso(1, 8),
    password: DEMO_PASSWORD,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000002',
    email: 'compliance@genex.pay',
    name: 'Chidi Compliance',
    role: 'compliance',
    lastLoginAt: iso(2, 14),
    password: DEMO_PASSWORD,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000003',
    email: 'finance@genex.pay',
    name: 'Femi Finance',
    role: 'finance',
    lastLoginAt: iso(1, 17),
    password: DEMO_PASSWORD,
  },
  {
    id: 'a1000000-0000-4000-8000-000000000004',
    email: 'support@genex.pay',
    name: 'Sade Support',
    role: 'support',
    lastLoginAt: iso(3, 11),
    password: DEMO_PASSWORD,
  },
];

export function seedFlags(): Flag[] {
  return [
    {
      key: 'gift_cards',
      enabled: false,
      description: 'Redeem gift cards to naira. Ships off; flip on to open the rail to customers.',
      updatedByAdminId: null,
      updatedAt: iso(9),
    },
    {
      key: 'crypto_sell',
      enabled: true,
      description: 'Sell crypto and settle to the naira wallet, the core rail.',
      updatedByAdminId: MOCK_ADMINS[0]!.id,
      updatedAt: iso(20),
    },
    {
      key: 'bank_transfer_out',
      enabled: true,
      description: 'Send naira out to any Nigerian bank account.',
      updatedByAdminId: MOCK_ADMINS[0]!.id,
      updatedAt: iso(20),
    },
    {
      key: 'bill_payments',
      enabled: true,
      description: 'Airtime, data, electricity, TV and betting top-ups.',
      updatedByAdminId: MOCK_ADMINS[0]!.id,
      updatedAt: iso(18),
    },
    {
      key: 'card_funding',
      enabled: true,
      description: 'Top up the wallet with a debit card via hosted checkout.',
      updatedByAdminId: MOCK_ADMINS[2]!.id,
      updatedAt: iso(6),
    },
    {
      key: 'referrals',
      enabled: false,
      description: 'Refer-a-friend rewards. Held back pending the growth launch.',
      updatedByAdminId: null,
      updatedAt: iso(30),
    },
    {
      key: 'maintenance_mode',
      enabled: false,
      description: 'Read-only mode for customers during a maintenance window.',
      updatedByAdminId: MOCK_ADMINS[0]!.id,
      updatedAt: iso(12),
    },
  ];
}

export function seedProviders(): ProviderConfig[] {
  return [
    {
      capability: 'payout',
      priority: ['paystack', 'flutterwave', 'korapay'],
      disabled: [],
      updatedByAdminId: MOCK_ADMINS[2]!.id,
      updatedAt: iso(4, 10),
    },
    {
      capability: 'collection',
      priority: ['flutterwave', 'korapay', 'paystack'],
      disabled: ['korapay'],
      updatedByAdminId: MOCK_ADMINS[2]!.id,
      updatedAt: iso(2, 16),
    },
    {
      capability: 'biller',
      priority: ['vtpass'],
      disabled: [],
      updatedByAdminId: null,
      updatedAt: iso(30),
    },
  ];
}

// ── Provider credentials & activation ──────────────────────────────────
/**
 * The vendor catalog, mirrored field-for-field from the API's
 * `modules/providers/catalog.ts`.
 *
 * It is duplicated rather than imported because the console holds no runtime dependency
 * on the API or on the contracts package. That makes drift possible, and drift here has
 * already cost this project two production-only bugs — so the rule is simple: a slug,
 * label, field key, `required`, `secret` or `envVar` that differs from the API's catalog
 * is a bug in this file, not a demo liberty. The *shape* is checked by `ProviderStatus`
 * at compile time; the *content* is checked by reading both lists side by side.
 */
export interface MockProviderField {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly secret: boolean;
  readonly required: boolean;
  readonly envVar: string | null;
}

export interface MockProviderDefinition {
  readonly slug: string;
  readonly label: string;
  readonly description: string;
  readonly services: readonly ProviderService[];
  readonly fields: readonly MockProviderField[];
  /**
   * Mirrored from the API catalogue's `webhook`, and the same rule applies: a `path` here
   * that no verifier answers to is a bug, not a demo liberty. The demo build never renders
   * an absolute URL — with no API there is no origin to name one against — but it does
   * render the dashboard line and the warnings, which is most of what the operator reads.
   */
  /**
   * Whether the vendor refuses to serve a customer it has not verified itself.
   *
   * Optional here and defaulted false, because it is true of exactly one vendor in the
   * catalogue and a required field would mean editing every entry to say "no".
   */
  readonly requiresCustomerVerification?: boolean;
  readonly webhook?: ProviderWebhook;
}

export const MOCK_PROVIDER_CATALOG: readonly MockProviderDefinition[] = [
  {
    slug: 'paystack',
    label: 'Paystack',
    description:
      'Naira payouts to bank accounts, dedicated virtual accounts and card collection. The bank directory is synced from here.',
    services: ['payout', 'collection'],
    fields: [
      {
        key: 'secretKey',
        label: 'Secret key',
        hint: 'Dashboard → Settings → API Keys & Webhooks → Secret Key (sk_live_…).',
        secret: true,
        required: true,
        envVar: 'PAYSTACK_SECRET_KEY',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank unless you front Paystack behind your own gateway.',
        secret: false,
        required: false,
        envVar: 'PAYSTACK_BASE_URL',
      },
    ],
    webhook: {
      path: '/webhooks/paystack',
      dashboard:
        'Paystack dashboard → Settings → API Keys & Webhooks → Webhook URL. The same screen the secret key comes from.',
      verified: true,
      warning:
        'Paystack retry for up to 72 hours, so the same event arriving several times is ordinary rather than a fault. Repeats are discarded on the event id; nothing is credited twice.',
    },
  },
  {
    slug: 'flutterwave',
    label: 'Flutterwave',
    description: 'Second naira rail. Used for payout and collection failover when Paystack is down.',
    services: ['payout', 'collection'],
    fields: [
      {
        key: 'secretKey',
        label: 'Secret key',
        hint: 'Dashboard → Settings → API → Secret Key (FLWSECK-…).',
        secret: true,
        required: true,
        envVar: 'FLUTTERWAVE_SECRET_KEY',
      },
      {
        key: 'secretHash',
        label: 'Webhook secret hash',
        hint: 'Dashboard → Settings → Webhooks → Secret hash. This is NOT the secret key; using the key here rejects every callback.',
        secret: true,
        required: true,
        envVar: 'FLUTTERWAVE_SECRET_HASH',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank for the vendor default.',
        secret: false,
        required: false,
        envVar: 'FLUTTERWAVE_BASE_URL',
      },
    ],
    webhook: {
      path: '/webhooks/flutterwave',
      dashboard:
        'Flutterwave dashboard → Settings → Webhooks → Webhook URL. The same screen the secret hash comes from.',
      verified: true,
      warning:
        'Only the v4 signing scheme is accepted. An account still on v3 sends a `verif-hash` header instead, and every callback on it is rejected. Move the account to v4 rather than asking for the older check back: v3 compares a shared secret for plain equality, so anyone who learns it can forge any body.',
    },
  },
  {
    slug: 'korapay',
    label: 'Korapay',
    description: 'Third naira rail. Payout and collection failover.',
    services: ['payout', 'collection'],
    fields: [
      {
        key: 'secretKey',
        label: 'Secret key',
        hint: 'Dashboard → Settings → API Configuration → Secret Key.',
        secret: true,
        required: true,
        envVar: 'KORAPAY_SECRET_KEY',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank for the vendor default.',
        secret: false,
        required: false,
        envVar: 'KORAPAY_BASE_URL',
      },
    ],
    webhook: {
      path: '/webhooks/korapay',
      dashboard:
        'Korapay dashboard → Settings → API Configuration is where the secret key lives. We could not confirm from Korapay’s own docs which field on that screen takes the callback URL, so check your dashboard rather than trusting a menu path from here.',
      verified: true,
      warning: null,
    },
  },
  {
    slug: 'vtpass',
    label: 'VTpass',
    description: 'Airtime, data, electricity, TV and other bill payments.',
    services: ['biller'],
    fields: [
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Dashboard → API Settings → API Key.',
        secret: true,
        required: true,
        envVar: 'VTPASS_API_KEY',
      },
      {
        key: 'secretKey',
        label: 'Secret key',
        hint: 'Dashboard → API Settings → Secret Key (SK_…).',
        secret: true,
        required: true,
        envVar: 'VTPASS_SECRET_KEY',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Sandbox and live are different hosts. Blank uses live.',
        secret: false,
        required: false,
        envVar: 'VTPASS_BASE_URL',
      },
    ],
    webhook: {
      path: '/webhooks/vtpass',
      dashboard:
        'VTpass dashboard → API Settings is where the API key lives. We could not confirm from VTpass’s own docs which field on that screen takes the callback URL, so check your dashboard rather than trusting a menu path from here.',
      verified: false,
      warning:
        'VTpass sign nothing. There is no signature on their callback to check, so anyone who learns this URL can post to it. That is safe only because of what happens next: the event is recorded as unverified and the delivery status is re-read from VTpass before anything is settled. A forged callback can at most make us ask VTpass a question we were going to ask anyway.',
    },
  },
  {
    slug: 'nowpayments',
    label: 'NOWPayments',
    description:
      'Crypto custody: deposit addresses, deposit detection and send-outs. Enabling this alone puts crypto live without touching any naira rail.',
    services: ['custody'],
    fields: [
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Dashboard → Settings → Payments → API keys. Issues addresses and reads status; it cannot move coin out on its own.',
        secret: true,
        required: true,
        envVar: 'NOWPAYMENTS_API_KEY',
      },
      {
        key: 'ipnSecret',
        label: 'IPN secret',
        hint: 'Dashboard → Settings → Payments → IPN secret key. Signs the callbacks they send us. A different value from the API key.',
        secret: true,
        required: true,
        envVar: 'NOWPAYMENTS_IPN_SECRET',
      },
      {
        key: 'email',
        label: 'Dashboard email',
        hint: 'Only needed to send crypto OUT: NOWPayments issues the payout JWT from the dashboard login.',
        secret: false,
        required: false,
        envVar: 'NOWPAYMENTS_EMAIL',
      },
      {
        key: 'password',
        label: 'Dashboard password',
        hint: 'Only needed to send crypto OUT. Leave blank on a deployment that never withdraws.',
        secret: true,
        required: false,
        envVar: 'NOWPAYMENTS_PASSWORD',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank for the vendor default.',
        secret: false,
        required: false,
        envVar: 'NOWPAYMENTS_BASE_URL',
      },
      {
        key: 'ipnCallbackUrl',
        label: 'IPN callback URL',
        hint: 'Where NOWPayments posts deposit notifications. Without it a deposit is only discoverable by polling.',
        secret: false,
        required: false,
        envVar: 'NOWPAYMENTS_IPN_CALLBACK_URL',
      },
    ],
    webhook: {
      path: '/webhooks/nowpayments',
      dashboard:
        'Not a dashboard setting. Paste it into the “IPN callback URL” field above. We send that value to NOWPayments on every address and payout request, so it is the URL their notifications actually go to. Their Settings → Payments screen is only where the IPN secret comes from.',
      verified: true,
      warning:
        'The IPN secret signs these callbacks and is a different credential from the API key. Set the API key here by mistake and every genuine callback is rejected while NOWPayments’ own delivery log shows a healthy endpoint returning 401. A deposit then only ever surfaces by polling.',
    },
  },
  {
    slug: 'custody_template',
    label: 'Generic custody (template)',
    description:
      'The generic REST custody adapter, for a vendor with no bespoke integration. Only used when NOWPayments is off.',
    services: ['custody'],
    fields: [
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'The vendor host the generic adapter calls.',
        secret: false,
        required: true,
        envVar: 'CUSTODY_PROVIDER_URL',
      },
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Bearer credential for the generic transport.',
        secret: true,
        required: false,
        envVar: 'PROVIDER_API_KEY',
      },
    ],
  },
  {
    slug: 'dojah',
    label: 'Dojah',
    description: 'Identity verification: BVN and NIN lookup with name and date-of-birth matching.',
    services: ['kyc'],
    fields: [
      {
        key: 'secretKey',
        label: 'Secret key',
        hint: 'Dashboard → API keys → Production secret key.',
        secret: true,
        required: true,
        envVar: 'DOJAH_SECRET_KEY',
      },
      {
        key: 'appId',
        label: 'App ID',
        hint: 'Dashboard → Apps. Sent as the AppId header.',
        secret: false,
        required: true,
        envVar: 'DOJAH_APP_ID',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Sandbox and production are different hosts.',
        secret: false,
        required: false,
        envVar: 'DOJAH_BASE_URL',
      },
    ],
  },
  {
    slug: 'smile_identity',
    label: 'Smile Identity',
    description: 'Identity verification and document checks across Africa.',
    services: ['kyc'],
    fields: [
      {
        key: 'partnerId',
        label: 'Partner ID',
        hint: 'Portal → Developer Tools. A short numeric id.',
        secret: false,
        required: true,
        envVar: 'SMILE_PARTNER_ID',
      },
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Portal → Developer Tools → API key. Base64, used to build the signature.',
        secret: true,
        required: true,
        envVar: 'SMILE_API_KEY',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Sandbox and production are different hosts.',
        secret: false,
        required: false,
        envVar: 'SMILE_BASE_URL',
      },
    ],
  },
  {
    slug: 'prembly',
    label: 'Prembly (Identitypass)',
    description: 'Identity verification: BVN, NIN and phone-number lookups.',
    services: ['kyc'],
    fields: [
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Dashboard → API keys → x-api-key.',
        secret: true,
        required: true,
        envVar: 'PREMBLY_API_KEY',
      },
      {
        key: 'appId',
        label: 'App ID',
        hint: 'Dashboard → Apps. Sent as app-id.',
        secret: false,
        required: true,
        envVar: 'PREMBLY_APP_ID',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank for the vendor default.',
        secret: false,
        required: false,
        envVar: 'PREMBLY_BASE_URL',
      },
    ],
  },
  {
    slug: 'youverify',
    label: 'Youverify',
    description: 'Identity verification: BVN, NIN and address checks.',
    services: ['kyc'],
    fields: [
      {
        key: 'apiKey',
        label: 'API token',
        hint: 'Dashboard → Developers → API token.',
        secret: true,
        required: true,
        envVar: 'YOUVERIFY_API_KEY',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Sandbox and production are different hosts.',
        secret: false,
        required: false,
        envVar: 'YOUVERIFY_BASE_URL',
      },
    ],
  },
  {
    slug: 'termii',
    label: 'Termii',
    description: 'Outbound SMS. One-time codes are delivered on this channel.',
    services: ['sms'],
    fields: [
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Dashboard → Settings → API key.',
        secret: true,
        required: true,
        envVar: 'TERMII_API_KEY',
      },
      {
        key: 'senderId',
        label: 'Sender ID',
        hint: 'The approved alphanumeric sender. An unapproved id silently fails to deliver.',
        secret: false,
        required: true,
        envVar: 'TERMII_SENDER_ID',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank for the vendor default.',
        secret: false,
        required: false,
        envVar: 'TERMII_BASE_URL',
      },
    ],
  },
  {
    slug: 'email',
    label: 'Transactional email',
    description: 'Receipts, one-time codes by email, support mirrors and operator notifications.',
    services: ['email'],
    fields: [
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Your email vendor API key (Resend: re_…).',
        secret: true,
        required: true,
        envVar: 'EMAIL_PROVIDER_API_KEY',
      },
      {
        key: 'from',
        label: 'From address',
        hint: 'Must be on a domain you have verified with the vendor.',
        secret: false,
        required: true,
        envVar: 'EMAIL_FROM',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Blank uses the Resend /emails endpoint.',
        secret: false,
        required: false,
        envVar: 'EMAIL_PROVIDER_BASE_URL',
      },
    ],
  },
  {
    slug: 'coingecko',
    label: 'CoinGecko market data',
    description:
      'The market price feed. Prices every coin in naira and derives the dollar rate from the same observation, so a quote and its FX leg are never seconds apart. The public tier needs no key.',
    services: ['rates'],
    fields: [
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Optional. A Demo or Pro key from coingecko.com raises the rate limit; the public tier works without one.',
        secret: true,
        required: false,
        envVar: 'COINGECKO_API_KEY',
      },
      {
        key: 'baseUrl',
        label: 'API base URL',
        hint: 'Leave blank for the public API. Pro subscriptions use a different host.',
        secret: false,
        required: false,
        envVar: 'COINGECKO_BASE_URL',
      },
    ],
  },
  {
    slug: 'rates',
    label: 'Market rates (custom feed)',
    description:
      'A bespoke feed that returns exact rationals, for a desk that runs its own pricing service. Only needed when CoinGecko is off.',
    services: ['rates'],
    fields: [
      {
        key: 'baseUrl',
        label: 'Feed base URL',
        hint: 'The market data endpoint the poller reads.',
        secret: false,
        required: true,
        envVar: 'RATE_PROVIDER_URL',
      },
      {
        key: 'apiKey',
        label: 'API key',
        hint: 'Bearer credential for the feed, if it needs one.',
        secret: true,
        required: false,
        envVar: 'PROVIDER_API_KEY',
      },
    ],
  },
  {
    slug: 'expo_push',
    label: 'Expo push',
    description: 'Push notification delivery to the mobile app.',
    services: ['push'],
    fields: [
      {
        key: 'accessToken',
        label: 'Access token',
        hint: 'expo.dev → Account → Access tokens. Optional: Expo accepts sends without one, but enhanced push security needs it.',
        secret: true,
        required: false,
        envVar: 'EXPO_ACCESS_TOKEN',
      },
    ],
  },
];

/**
 * The capability order the API's `listProviderStatus` returns, kept verbatim so the
 * summary strip reads the same against the mock and against a real deployment.
 */
export const MOCK_PROVIDER_SERVICES: readonly ProviderService[] = [
  'payout',
  'collection',
  'biller',
  'custody',
  'kyc',
  'sms',
  'email',
  'rates',
  'push',
];

/**
 * The demo deployment's `.env`, keyed by the catalog's `envVar` names.
 *
 * Deliberately non-empty: "this value is coming from the environment, not from the
 * console" is a state an operator has to be able to recognise (they cannot change it
 * here without overriding it), and a mock that never produced it would leave that branch
 * of the page untested until someone hit it in production.
 */
export const MOCK_PROVIDER_ENV: Readonly<Record<string, string>> = {
  VTPASS_API_KEY: 'vtp_live_env_7c21b8',
  TERMII_API_KEY: 'TLenvKEY_5b90ac',
  DOJAH_BASE_URL: 'https://api.dojah.io',
};

/**
 * What a provider with no console row falls back to, and which deployment this is.
 *
 * `live` + `production` is the combination the API's own env validation forces on a real
 * deployment (`PROVIDER_MODE=mock` with nothing named in `SIMULATED_PROVIDERS` is
 * refused at boot in production), so it is the combination the demo shows. It is also
 * the one worth showing: it is where a simulated capability *refuses* rather than
 * quietly faking, and where the money-moving warning appears.
 */
export const MOCK_PROVIDER_DEFAULT_MODE: 'mock' | 'live' = 'live';
export const MOCK_PROVIDER_ENVIRONMENT: 'development' | 'test' | 'production' = 'production';

/** One provider's stored row — the mock's stand-in for the `provider_credentials` table. */
export interface MockProviderRow {
  enabled: boolean;
  /**
   * Plaintext here only because this file *is* the server side of the seam. The console
   * never receives these: the transport masks them on the way out exactly as the API
   * does, so no page can render one even by accident.
   */
  secrets: Record<string, string>;
  updatedByAdminId: string | null;
  updatedAt: string | null;
}

/**
 * Stored provider rows, seeded across every state the page has to tell apart: live,
 * enabled-but-missing-a-required-key, configured-but-switched-off, and never set up.
 * Two capabilities (custody and rates) are left with no live provider, so the summary
 * strip shows both the green and the simulated case.
 */
export function seedProviderCredentials(): Record<string, MockProviderRow> {
  const by = (adminIndex: number, daysAgo: number): Pick<MockProviderRow, 'updatedByAdminId' | 'updatedAt'> => ({
    updatedByAdminId: MOCK_ADMINS[adminIndex]!.id,
    updatedAt: iso(daysAgo, 11),
  });
  return {
    paystack: { enabled: true, secrets: { secretKey: 'sk_live_9f3c7a21b8e04f2a' }, ...by(2, 12) },
    // Enabled with the webhook hash still missing — the state the page must not let an
    // operator mistake for "on", because the switch is on and nothing is working.
    flutterwave: { enabled: true, secrets: { secretKey: 'FLWSECK-4b71c0d9e6a3' }, ...by(2, 9) },
    korapay: { enabled: false, secrets: {}, ...by(2, 30) },
    vtpass: { enabled: true, secrets: { secretKey: 'SK_c40e1f77ab92' }, ...by(0, 15) },
    // Fully configured and deliberately switched off: crypto custody stays simulated
    // until someone flips it, which is exactly the per-vendor decision this page exists for.
    nowpayments: {
      enabled: false,
      secrets: { apiKey: 'NP-7GQ2X9ZK4M1D', ipnSecret: 'ipn_2f8b6c4a90e1' },
      ...by(0, 26),
    },
    custody_template: { enabled: false, secrets: {}, updatedByAdminId: null, updatedAt: null },
    dojah: { enabled: true, secrets: { secretKey: 'prod_sk_1d90fe33c7b5', appId: '64f0a9c2b71e' }, ...by(1, 22) },
    smile_identity: { enabled: false, secrets: {}, updatedByAdminId: null, updatedAt: null },
    prembly: { enabled: false, secrets: {}, updatedByAdminId: null, updatedAt: null },
    youverify: { enabled: false, secrets: {}, updatedByAdminId: null, updatedAt: null },
    termii: { enabled: true, secrets: { senderId: 'GenExPay' }, ...by(0, 8) },
    email: {
      enabled: true,
      secrets: { apiKey: 're_8Ha2Kd91Pq47', from: 'no-reply@genexpay.com' },
      ...by(0, 19),
    },
    // CoinGecko has no required field — the public tier works with no key — so enabling
    // it is enough to make it live. That is also the only reason the bespoke feed below
    // can sit switched off without leaving quotes on the built-in table.
    coingecko: { enabled: true, secrets: {}, ...by(2, 5) },
    rates: { enabled: false, secrets: {}, updatedByAdminId: null, updatedAt: null },
    expo_push: { enabled: true, secrets: { accessToken: 'expo_tok_5512aabf' }, ...by(1, 4) },
  };
}

/** Mirrors the API's default `RATE_MAX_AGE_SECONDS`, so the demo's ceiling bites where the real one does. */
export const MOCK_RATE_MAX_AGE_SECONDS = 300;

/** Anchored to the clock, not to the fixed demo date — freshness is the whole point here. */
const secondsAgo = (seconds: number): string => new Date(Date.now() - seconds * 1000).toISOString();

/**
 * The live-rate board.
 *
 * Deliberately seeded into all three states an operator has to be able to tell apart at a
 * glance: pairs pricing off a fresh feed, one pair carrying a standing override, and one
 * whose observation has aged past the ceiling and so prices nothing at all. Rationals are
 * in lowest terms because that is what the API stores — `Rate.of` reduces on the way in.
 */
export function seedRateBoard(): AdminRateRow[] {
  return [
    // 1 USD = ₦1,705.00
    {
      base: 'USD',
      quote: 'NGN',
      live: { numerator: '1705', denominator: '1', changeBps: 12, observedAt: secondsAgo(24), stale: false },
      override: null,
      effective: { numerator: '1705', denominator: '1' },
    },
    // 1 BTC = ₦148,320,500.00
    {
      base: 'BTC',
      quote: 'NGN',
      live: {
        numerator: '148320500',
        denominator: '1',
        changeBps: -240,
        observedAt: secondsAgo(31),
        stale: false,
      },
      override: null,
      effective: { numerator: '148320500', denominator: '1' },
    },
    // 1 ETH = ₦5,412,900.50, but the feed stopped twenty minutes ago — nothing prices this.
    {
      base: 'ETH',
      quote: 'NGN',
      live: {
        numerator: '10825801',
        denominator: '2',
        changeBps: 88,
        observedAt: secondsAgo(20 * 60),
        stale: true,
      },
      override: null,
      effective: null,
    },
    // 1 SOL = ₦342,480.00 on the feed, held at ₦345,000.00 by finance for margin.
    {
      base: 'SOL',
      quote: 'NGN',
      live: { numerator: '342480', denominator: '1', changeBps: 305, observedAt: secondsAgo(19), stale: false },
      override: {
        numerator: '345000',
        denominator: '1',
        setByAdminId: MOCK_ADMINS[2]!.id,
        setAt: secondsAgo(52 * 60),
        expiresAt: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString(),
      },
      effective: { numerator: '345000', denominator: '1' },
    },
    // 1 USDT = ₦1,712.40
    {
      base: 'USDT',
      quote: 'NGN',
      live: { numerator: '8562', denominator: '5', changeBps: 4, observedAt: secondsAgo(17), stale: false },
      override: null,
      effective: { numerator: '8562', denominator: '5' },
    },
  ];
}

const FIRST = ['Emeka', 'Ngozi', 'Tunde', 'Aisha', 'Kunle', 'Bola', 'Ifeoma', 'Musa', 'Yemi', 'Zainab', 'Obi', 'Halima'];
const LAST = ['Okafor', 'Adeyemi', 'Balogun', 'Danladi', 'Eze', 'Okonkwo', 'Bello', 'Ojo', 'Nwosu', 'Sani'];
const TIERS = ['tier_1', 'tier_2', 'tier_2', 'tier_3', 'tier_0'] as const;
/**
 * The demo's status cycle, covering all five states the contract now carries.
 *
 * Weighted towards active, because that is what a real base looks like, but every state is
 * present: an operator opening the demo has to be able to see a lockout, a suspension, a
 * dormant account and a closed one without having to create them, and `closed` in particular
 * is the state whose controls are all disabled and which is therefore easiest to ship broken.
 */
const STATUSES = [
  'active',
  'active',
  'active',
  'suspended',
  'locked',
  'active',
  'inactive',
  'active',
  'closed',
] as const;

function maskPhone(seed: number): string {
  const a = 700 + (seed % 99);
  const d = 1000 + ((seed * 37) % 8999);
  return `+234 ${a} ••• ${d}`;
}

/**
 * The same number, whole.
 *
 * The middle three digits the mask hides are generated from the same seed, so the full number
 * and the mask agree everywhere they appear side by side. Operator screens are the deliberate
 * exception to the app's masking rule, and the demo has to be able to show that exception
 * working rather than only describe it.
 */
function fullPhone(seed: number): string {
  const a = 700 + (seed % 99);
  const mid = 100 + ((seed * 53) % 899);
  const d = 1000 + ((seed * 37) % 8999);
  return `+234${a}${mid}${d}`;
}

/**
 * Which seeded rows carry a full number and which carry only a mask.
 *
 * Deliberately not all of them. The console has to behave correctly against an API that has
 * not shipped the full-phone field yet, and the only way anybody ever sees that path is if the
 * demo contains it. Roughly one row in four is masked-only, so every table shows both states
 * at once and the "the API has not sent the full number" line is visible without contriving
 * anything.
 */
function hasFullPhone(seed: number): boolean {
  return seed % 4 !== 3;
}

function phoneFields(seed: number): { phone: string | null } {
  return { phone: hasFullPhone(seed) ? fullPhone(seed) : null };
}

const kobo = (naira: number): { currency: 'NGN'; amount: string } => ({
  currency: 'NGN',
  amount: String(Math.round(naira * 100)),
});

/**
 * The photos three of the seeded customers have set.
 *
 * Three rather than all of them, and not the first three: an avatar is optional in the
 * contract and most customers never set one, so a demo where every row had a face would make
 * the lettermark fallback — which is what an operator will actually see most of the time —
 * invisible. Keyed by the customer's index so the mock's photo route can find them.
 */
const DEMO_AVATAR_BY_INDEX: Readonly<Record<number, { mime: 'image/png'; dataBase64: string }>> = {
  1: { mime: 'image/png', dataBase64: DEMO_AVATAR_VIOLET },
  4: { mime: 'image/png', dataBase64: DEMO_AVATAR_GREEN },
  9: { mime: 'image/png', dataBase64: DEMO_AVATAR_AMBER },
};

/** The photo bytes the mock media route serves, keyed by customer id. */
export const USER_PHOTOS: Record<string, { mime: string; name: string; dataBase64: string }> = {};

export function seedUsers(): AdminUserDetail[] {
  const users: AdminUserDetail[] = [];
  for (const key of Object.keys(USER_PHOTOS)) delete USER_PHOTOS[key];
  for (let i = 0; i < 24; i += 1) {
    const first = FIRST[i % FIRST.length]!;
    const last = LAST[i % LAST.length]!;
    const tier = TIERS[i % TIERS.length]!;
    const status = STATUSES[i % STATUSES.length]!;
    const daily = 250000 + i * 12500;
    const id = `u0000000-0000-4000-8000-${String(100000 + i).padStart(12, '0')}`;
    const photo = DEMO_AVATAR_BY_INDEX[i];
    if (photo !== undefined) {
      USER_PHOTOS[id] = { ...photo, name: `${first.toLowerCase()}-${last.toLowerCase()}.png` };
    }
    users.push({
      id,
      maskedPhone: maskPhone(i * 7 + 3),
      ...phoneFields(i * 7 + 3),
      /*
       * `path` mirrors what `/auth/me` returns for the customer's own photo: API-relative,
       * with the `/v1` prefix and a version stamp. The console does not fetch it from here —
       * the photo route is bearer-scoped, so it goes through the admin media path — but the
       * field is the signal that a photo exists at all, and its `updatedAt` is what busts the
       * cache when one is replaced.
       */
      avatar:
        photo === undefined
          ? null
          : { path: `/v1/admin/users/${id}/photo?v=${String(1787136339000 + i)}`, mime: photo.mime, updatedAt: iso(i % 9, 12) },
      displayName: `${first} ${last}`,
      // Unmasked, like the API: these are the handles an operator actually acts on.
      email: `${first}.${last}${i}@example.test`.toLowerCase(),
      username: `${first}${last}${i}`.toLowerCase(),
      tier,
      status,
      createdAt: iso(40 - i, 10),
      kycStatus: tier === 'tier_0' ? 'none' : tier === 'tier_1' ? 'pending' : 'verified',
      limits: { single: kobo(500000), daily: kobo(1000000), monthly: kobo(5000000) },
      usage: { daily: kobo(daily), monthly: kobo(daily * 6) },
      walletBalance: kobo(15000 + i * 4200),
      lastActiveAt: iso(i % 9, 12),
      // Two live sessions and one signed out, so the demo console shows the state support
      // actually asks about rather than a single happy row.
      sessions: [
        {
          id: `d0000000-0000-4000-8000-${String(200000 + i * 3).padStart(12, '0')}`,
          name: i % 2 === 0 ? 'Pixel 8' : 'iPhone 15',
          platform: i % 2 === 0 ? 'android' : 'ios',
          label: i % 2 === 0 ? 'GenEx Pay on Android' : 'GenEx Pay on iOS',
          lastSeenLocation: 'Lagos, NG',
          lastSeenAt: iso(i % 9, 12),
          revokedAt: null,
        },
        {
          id: `d0000000-0000-4000-8000-${String(200001 + i * 3).padStart(12, '0')}`,
          name: 'Infinix Hot 40',
          platform: 'android',
          label: 'GenEx Pay on Android',
          lastSeenLocation: 'Abuja, NG',
          lastSeenAt: iso(30 + i, 9),
          revokedAt: iso(20 + i, 9),
        },
      ],
      suspendedReason: status === 'suspended' ? 'Manual review: unusual payout velocity.' : null,
      // `lockedUntil` is a timed lockout's expiry, so it is a *future* timestamp. Seeding it in
      // the past would render as a lockout that has already lapsed, which is the one state this
      // field cannot be in while `status` still says locked.
      lockedUntil: status === 'locked' ? isoAhead(2 + (i % 6)) : null,
      inactiveAt: status === 'inactive' ? iso(i % 9, 11) : null,
      // Independent of every account state, which is exactly why it is its own field: an
      // account can be perfectly active and still have a customer who cannot get past the PIN.
      pinResetRequired: status === 'locked' || i % 11 === 5,
      /*
       * Every balance, not only naira.
       *
       * A customer holding a coin balance as well is the normal case, and it is the reason the
       * credit and debit controls need a currency picker rather than assuming kobo. Seeding
       * naira-only would have let a page ship that silently adjusted the wrong wallet.
       */
      balances:
        i % 3 === 0
          ? [kobo(15000 + i * 4200)]
          : [kobo(15000 + i * 4200), { currency: 'USDT' as const, amount: String(1200 + i * 310) }],
      kycDocuments: seedKycDocumentsFor(i),
    });
  }
  return users;
}

/**
 * The automated chain's verdict on one queued submission.
 *
 * Cycled rather than randomised so the demo build is deterministic, and shaped to cover
 * the four dispositions that reach a human: a name that did not match, a register with no
 * record, a match too thin for the tier asked for, and no vendor available at all. The
 * last of those is deliberately included — a queue that only ever shows vendor answers
 * hides the case where the identity rail itself is down.
 */
function kycProvenance(i: number): Pick<
  AdminKycQueueItem,
  'decidedBy' | 'providerOutcome' | 'providerSlug' | 'providerReference' | 'autoCheckedAt'
> {
  const outcomes = ['mismatch', 'not_found', 'verified', 'unsupported'] as const;
  const outcome = outcomes[i % outcomes.length]!;
  const answered = outcome !== 'unsupported';
  return {
    // Null while pending: `decidedBy` records who DECIDED, and nobody has yet.
    decidedBy: null,
    providerOutcome: outcome,
    providerSlug: answered ? (i % 2 === 0 ? 'dojah' : 'prembly') : null,
    providerReference: answered ? `JOB-${String(90000 + i * 7)}` : null,
    autoCheckedAt: iso(i, 8 + i),
  };
}

/**
 * The document bytes, keyed by **document** id rather than by submission.
 *
 * That is the contract's own arrangement and it is worth mirroring exactly: a submission can
 * carry several documents, the bytes route is addressed by the document, and the audit row it
 * writes names the one that was actually opened. A mock keyed by submission would have let a
 * viewer ship that fetched "the document" and quietly showed the first of four.
 *
 * One of them is a PDF and the rest are images, because a reviewer meets both and a viewer
 * only ever tested against an image renders a broken frame the first time somebody uploads a
 * scan from their bank. One customer has no documents at all, which is the third state the
 * card has to handle and the one most likely to be forgotten.
 */
export const KYC_DOCUMENTS: Record<string, { mime: string; name: string; dataBase64: string }> = {};

/** A hex digest that is the right shape without pretending to be a real hash of anything. */
function fakeSha(seed: number): string {
  let out = '';
  for (let i = 0; i < 64; i += 1) out += (((seed + 1) * (i + 7) * 2654435761) % 16).toString(16);
  return out;
}

/**
 * The identity documents on one customer's file, and their bytes.
 *
 * Cycled rather than randomised so the demo is the same on every reload: the first customer
 * has a slip and a selfie, the second a PDF, and every third customer has nothing on file.
 */
function seedKycDocumentsFor(i: number): KycDocumentSummary[] {
  if (i % 3 === 2 || i >= 12) return [];
  const submissionId = `k0000000-0000-4000-8000-${String(200000 + (i % 6)).padStart(12, '0')}`;
  const shapes: ReadonlyArray<{
    kind: KycDocumentSummary['kind'];
    mime: KycDocumentSummary['mime'];
    name: string;
    data: string;
  }> =
    i % 3 === 1
      ? [{ kind: 'nin_slip', mime: 'application/pdf', name: 'nin-slip.pdf', data: DEMO_KYC_PDF }]
      : [
          { kind: 'bvn_slip', mime: 'image/png', name: 'bvn-slip.png', data: DEMO_KYC_SCAN },
          { kind: 'selfie', mime: 'image/png', name: 'selfie.png', data: DEMO_AVATAR_VIOLET },
        ];

  return shapes.map((shape, n) => {
    const id = `kd000000-0000-4000-8000-${String(400000 + i * 4 + n).padStart(12, '0')}`;
    KYC_DOCUMENTS[id] = { mime: shape.mime, name: shape.name, dataBase64: shape.data };
    return {
      id,
      submissionId,
      kind: shape.kind,
      mime: shape.mime,
      // Decoded length: base64 without padding is three bytes per four characters.
      byteLength: Math.max(1, Math.floor((shape.data.replace(/=+$/, '').length * 3) / 4)),
      sha256: fakeSha(i * 4 + n),
      uploadedAt: iso(i + 1, 10),
    };
  });
}

/**
 * The document number as the contract publishes it: **masked**, never the raw value.
 *
 * `adminKycDetailSchema.documentNumber` landed as `*******4291` and carries a long argument
 * for why it will not land as anything else — a BVN is a lifetime identifier that unlocks
 * every bank account a Nigerian holds and cannot be rotated after a leak, so the raw value
 * has no reason to reach a browser. The reviewer's question is answered by the audited
 * document image against the length and the last four, which is exactly what this is.
 *
 * The seed produces the shape rather than a number, so the demo build shows an operator what
 * they will actually see and never trains anybody to expect eleven readable digits.
 */
function documentNumberOf(last4: string): string {
  return `${'*'.repeat(7)}${last4}`;
}

export function seedKycQueue(): AdminKycDetail[] {
  const kinds = ['bvn', 'nin'] as const;
  const tiers = ['tier_2', 'tier_3'] as const;
  const items: AdminKycDetail[] = [];
  for (let i = 0; i < 6; i += 1) {
    const first = FIRST[(i * 3) % FIRST.length]!;
    const last = LAST[(i * 5) % LAST.length]!;
    const id = `k0000000-0000-4000-8000-${String(200000 + i).padStart(12, '0')}`;
    const last4 = String(1000 + ((i * 2237) % 8999)).slice(-4);
    items.push({
      id,
      userId: `u0000000-0000-4000-8000-${String(100000 + i).padStart(12, '0')}`,
      maskedPhone: maskPhone(i * 11 + 5),
      ...phoneFields(i * 11 + 5),
      displayName: `${first} ${last}`,
      targetTier: tiers[i % 2]!,
      documentKind: kinds[i % 2]!,
      documentLast4: last4,
      /*
       * Present on four of the six and null on the other two, which is the real pair of
       * states: a submission can hold no document value at all. Never absent — the field is
       * published now, so a row without it is a server that is wrong rather than a server
       * that is behind.
       */
      documentNumber: i % 3 === 2 ? null : documentNumberOf(last4),
      status: 'pending',
      reason: null,
      reviewedAt: null,
      reviewedByAdminId: null,
      dateOfBirth: `199${i % 9}-0${(i % 8) + 1}-1${i % 9}`,
      submittedAt: iso(i, 8 + i),
      // Why each one is in front of a human, cycled across the real outcomes so the queue
      // in the demo build exercises every triage path a reviewer will actually meet —
      // including the one where no vendor answered at all.
      ...kycProvenance(i),
    });
  }
  return items;
}

const EVENT_KINDS = [
  'crypto_sell',
  'bank_funding',
  'transfer_out',
  'bill_payment',
  'card_funding',
  'giftcard_redeem',
  'refund',
] as const;
const EVENT_STATUSES = ['settled', 'settled', 'settled', 'pending', 'failed', 'reversed'] as const;
const PROVIDERS = ['paystack', 'flutterwave', 'korapay', null] as const;

export function seedTransactions(): AdminTxRow[] {
  const events: AdminTxRow[] = [];
  for (let i = 0; i < 46; i += 1) {
    const kind = EVENT_KINDS[i % EVENT_KINDS.length]!;
    const status = EVENT_STATUSES[i % EVENT_STATUSES.length]!;
    const amount = 5000 + ((i * 8731) % 240000);
    const provider = PROVIDERS[i % PROVIDERS.length] ?? null;
    events.push({
      id: `t0000000-0000-4000-8000-${String(300000 + i).padStart(12, '0')}`,
      kind,
      /*
       * A vendor identifier on the rows that would have one, none on the rest.
       *
       * Every row carrying one would make the column look uniform in the mock and ragged
       * against the real API, which is the sort of difference that gets designed around and
       * then surprises somebody. Roughly every third row, which is nearer the truth.
       */
      externalRefs:
        i % 3 === 0
          ? [{ label: 'Gateway transaction id', value: String(6480317111 + i) }]
          : [],
      userId: `u0000000-0000-4000-8000-${String(100000 + (i % 24)).padStart(12, '0')}`,
      maskedPhone: maskPhone(i * 13 + 1),
      ...phoneFields(i * 13 + 1),
      amount: kobo(amount),
      status,
      provider,
      /*
       * What the vendor charged *us*, which is not what we charged the customer.
       *
       * `null` on a movement no vendor billed, and null on a slice of the rows that did go
       * through one — because a cost is not always known when the row is. A network fee is
       * known at broadcast; a PSP's cut sometimes only on settlement. The column has to read
       * as "not captured" rather than as "no fee", and a blank cell that silently means zero
       * on a money page is exactly the quiet lie this console is built to avoid.
       *
       * The naira figure is a flat 50 kobo plus 30 basis points of the amount, which is close
       * enough to how a Nigerian payment vendor actually prices to be worth reading.
       */
      providerCost:
        provider === null || i % 5 === 4
          ? null
          : {
              amount: kobo(Math.round((0.5 + amount * 0.003) * 100) / 100),
              vendor: provider,
              capturedAt: iso(Math.floor(i / 3), 6 + (i % 12)),
            },
      reference: `GX-${(1000000 + i * 7919).toString(36).toUpperCase()}`,
      createdAt: iso(Math.floor(i / 3), 6 + (i % 12)),
    });
  }
  return events;
}

/**
 * The ledger reconciliation view: each account's cached balance against the sum of its
 * postings. A house account carries a null `userId`; a customer wallet carries theirs.
 * Most reconcile exactly (drift zero); a couple are seeded with a small drift to show
 * the flagged state.
 */
export function seedReconciliation(): ReconciliationRow[] {
  const accounts: ReadonlyArray<{ kind: string; currency: string; userId: string | null; balance: number; driftKobo: number }> = [
    { kind: 'house_revenue', currency: 'NGN', userId: null, balance: 8_450_000, driftKobo: 0 },
    { kind: 'external_bank', currency: 'NGN', userId: null, balance: 21_900_000, driftKobo: 0 },
    { kind: 'external_giftcard', currency: 'NGN', userId: null, balance: 3_120_000, driftKobo: 0 },
    { kind: 'external_crypto', currency: 'NGN', userId: null, balance: 14_070_000, driftKobo: -450 },
    { kind: 'user_wallet', currency: 'NGN', userId: 'u0000000-0000-4000-8000-000000100001', balance: 62_500, driftKobo: 0 },
    { kind: 'user_wallet', currency: 'NGN', userId: 'u0000000-0000-4000-8000-000000100002', balance: 148_200, driftKobo: 0 },
    { kind: 'user_wallet', currency: 'NGN', userId: 'u0000000-0000-4000-8000-000000100003', balance: 5_900, driftKobo: 320 },
    { kind: 'user_wallet', currency: 'NGN', userId: 'u0000000-0000-4000-8000-000000100004', balance: 233_400, driftKobo: 0 },
  ];
  return accounts.map((a, i) => {
    const cachedKobo = a.balance * 100;
    const postingsKobo = cachedKobo - a.driftKobo;
    return {
      account: {
        id: `ac000000-0000-4000-8000-${String(500000 + i).padStart(12, '0')}`,
        kind: a.kind,
        currency: a.currency,
        userId: a.userId,
      },
      cachedBalance: { currency: 'NGN', amount: String(cachedKobo) },
      postingsSum: { currency: 'NGN', amount: String(postingsKobo) },
      drift: { currency: 'NGN', amount: String(a.driftKobo) },
    };
  });
}

export function seedBrand(): BrandAsset | null {
  return null;
}

/**
 * No splash to begin with — the same starting point as the logo, and for the same reason:
 * the state a fresh deployment is actually in is "nothing uploaded", and that is the state
 * whose UI has broken before. The API answers 404 there, so the mock must too.
 */
export function seedSplash(): SplashAsset | null {
  return null;
}

// ── Bank directory ─────────────────────────────────────────────────────

/**
 * A handful of real Nigerian institutions with their real NIBSS codes, so the demo page
 * looks like the live one. `logoPath` is null across the board: the mock backend stores no
 * bytes, so every row demonstrates the placeholder state an operator is there to fix.
 *
 * `Sterling Bank` is delisted on purpose — the row an operator most needs to notice is the
 * one the provider stopped listing, and a demo that only shows the happy state hides it.
 */
/**
 * Three banners covering the states the page has to render differently: one running, one
 * paused, and one whose campaign has already ended. The last is the important seed — it is
 * the case an operator has to be able to *see*, since a finished banner leaves the app
 * silently and the console is the only place that says so.
 *
 * No artwork: the mock stores no bytes, so every row starts on its placeholder.
 *
 * Every seed carries a colour pair that passes `checkBannerLegibility`, and the third also
 * carries an external destination — so the two newest halves of this page (the contrast
 * preview and the browser link) are both exercised the moment the demo console opens, rather
 * than only after somebody happens to create a banner.
 */
export function seedBanners(): AdminBanner[] {
  return [
    {
      id: 'a0000000-0000-4000-8000-000000000001',
      title: 'Pay bills, keep the change',
      body: 'Airtime, data, power and TV, settled from your naira balance.',
      deepLink: 'bills',
      backgroundColor: '#1652F0',
      textColor: '#FFFFFF',
      externalUrl: null,
      imageMime: null,
      imagePath: null,
      active: true,
      sortOrder: 0,
      startsAt: null,
      endsAt: null,
      live: true,
      updatedByAdminId: MOCK_ADMINS[0]?.id ?? null,
      createdAt: iso(20),
      updatedAt: iso(4),
    },
    {
      id: 'a0000000-0000-4000-8000-000000000002',
      title: 'Sell crypto in seconds',
      body: 'Live rates, naira in your wallet before the page finishes loading.',
      deepLink: 'sell',
      backgroundColor: '#0E7A4F',
      textColor: '#FFFFFF',
      externalUrl: null,
      imageMime: null,
      imagePath: null,
      active: true,
      sortOrder: 1,
      startsAt: null,
      endsAt: null,
      live: true,
      updatedByAdminId: MOCK_ADMINS[0]?.id ?? null,
      createdAt: iso(18),
      updatedAt: iso(18),
    },
    {
      id: 'a0000000-0000-4000-8000-000000000003',
      title: 'Independence week bonus',
      body: 'Zero fees on every bank withdrawal all week.',
      deepLink: 'convert',
      backgroundColor: '#B4471F',
      textColor: '#FFFFFF',
      // The one seed that leaves the app. `deepLink` stays set underneath it, so a client
      // that does not understand `externalUrl` still has somewhere safe to go.
      externalUrl: 'https://genxpay.com.ng/independence',
      imageMime: null,
      imagePath: null,
      active: true,
      sortOrder: 2,
      startsAt: iso(30),
      endsAt: iso(9),
      // Still `active`, and still gone from every phone: the window closed. Exactly the
      // distinction the page has to make legible.
      live: false,
      updatedByAdminId: MOCK_ADMINS[0]?.id ?? null,
      createdAt: iso(31),
      updatedAt: iso(30),
    },
  ];
}

export function seedBanks(): AdminBank[] {
  const rows: readonly { code: string; name: string; active?: boolean }[] = [
    { code: '044', name: 'Access Bank' },
    { code: '023', name: 'Citibank Nigeria' },
    { code: '050', name: 'Ecobank Nigeria' },
    { code: '070', name: 'Fidelity Bank' },
    { code: '011', name: 'First Bank of Nigeria' },
    { code: '058', name: 'Guaranty Trust Bank' },
    { code: '082', name: 'Keystone Bank' },
    { code: '076', name: 'Polaris Bank' },
    { code: '221', name: 'Stanbic IBTC Bank' },
    { code: '232', name: 'Sterling Bank', active: false },
    { code: '032', name: 'Union Bank of Nigeria' },
    { code: '033', name: 'United Bank for Africa' },
    { code: '215', name: 'Unity Bank' },
    { code: '035', name: 'Wema Bank' },
    { code: '057', name: 'Zenith Bank' },
  ];
  return rows.map((row, i) => ({
    id: `b0000000-0000-4000-8000-${String(700000 + i).padStart(12, '0')}`,
    code: row.code,
    name: row.name,
    active: row.active ?? true,
    logoMime: null,
    logoPath: null,
    logoUpdatedAt: null,
    logoUpdatedByAdminId: null,
    syncedAt: iso(0, 3),
  }));
}

// ── Gift cards ──────────────────────────────────────────────────────────────────

/** Kobo per one whole face unit, as the contract's rational rate. */
const gcRate = (nairaPerUnit: number) => ({ numerator: String(nairaPerUnit * 100), denominator: '1' });
/** A face amount in USD cents. */
const usd = (dollars: number) => ({ currency: 'USD' as const, amount: String(dollars * 100) });

/**
 * The brand catalogue as the *operator* route returns it: `AdminGiftcardBrand`, which is
 * the customer's `GiftcardBrand` plus `sortOrder` and `createdAt`.
 *
 * The admin shape and not the customer one, because the mock is the API in a demo build and
 * a mock that served the narrower shape is exactly how this console has shipped a page that
 * looked healthy offline and broke against the real deployment. The last brand is seeded
 * *inactive* on purpose: switching one back on is the thing this tab exists for, and a demo
 * that only shows live brands hides the state an operator most needs to find.
 */
export function seedGiftcardBrands(): AdminGiftcardBrand[] {
  return [
    {
      id: 'gb000000-0000-4000-8000-000000000001',
      name: 'Amazon US',
      category: 'shopping',
      country: 'US',
      faceCurrency: 'USD',
      rateNairaPerUnit: gcRate(1200),
      minFace: usd(10),
      maxFace: usd(200),
      active: true,
      logoKey: null,
      sortOrder: 0,
      createdAt: iso(40),
    },
    {
      id: 'gb000000-0000-4000-8000-000000000002',
      name: 'iTunes US',
      category: 'entertainment',
      country: 'US',
      faceCurrency: 'USD',
      rateNairaPerUnit: gcRate(950),
      minFace: usd(10),
      maxFace: usd(200),
      active: true,
      logoKey: null,
      sortOrder: 1,
      createdAt: iso(40),
    },
    {
      id: 'gb000000-0000-4000-8000-000000000003',
      name: 'Steam US',
      category: 'gaming',
      country: 'US',
      faceCurrency: 'USD',
      rateNairaPerUnit: gcRate(1100),
      minFace: usd(10),
      maxFace: usd(200),
      active: true,
      logoKey: null,
      sortOrder: 2,
      createdAt: iso(39),
    },
    {
      id: 'gb000000-0000-4000-8000-000000000004',
      name: 'Google Play US',
      category: 'entertainment',
      country: 'US',
      faceCurrency: 'USD',
      rateNairaPerUnit: gcRate(900),
      minFace: usd(10),
      maxFace: usd(500),
      active: true,
      logoKey: null,
      sortOrder: 3,
      createdAt: iso(38),
    },
    {
      id: 'gb000000-0000-4000-8000-000000000005',
      name: 'Apple US',
      category: 'entertainment',
      country: 'US',
      faceCurrency: 'USD',
      rateNairaPerUnit: gcRate(1000),
      minFace: usd(10),
      maxFace: usd(500),
      active: false,
      logoKey: null,
      sortOrder: 4,
      createdAt: iso(38),
    },
  ];
}

// ── KYC tier ceilings ───────────────────────────────────────────────────────────

/**
 * The four tiers' ceilings, seeded with the contract's `TIER_LIMITS` figures — the same
 * numbers migration `0010_kyc_limits.sql` writes into `kyc_tier_limits`, so the demo board
 * opens on what a freshly migrated deployment actually holds.
 *
 * `tier_0` is all zeros and that is not a placeholder: a registered-but-unverified account
 * cannot move money at all, and the ceilings are how that is enforced.
 */
export function seedTierLimits(): TierLimit[] {
  const ngn = (kobo: string) => ({ currency: 'NGN' as const, amount: kobo });
  return [
    { tier: 'tier_0', single: ngn('0'), daily: ngn('0'), monthly: ngn('0'), updatedAt: null, updatedByAdminId: null },
    {
      tier: 'tier_1',
      single: ngn('2000000'),
      daily: ngn('5000000'),
      monthly: ngn('20000000'),
      updatedAt: null,
      updatedByAdminId: null,
    },
    {
      tier: 'tier_2',
      single: ngn('50000000'),
      daily: ngn('100000000'),
      monthly: ngn('500000000'),
      updatedAt: iso(12, 10),
      updatedByAdminId: MOCK_ADMINS[1]!.id,
    },
    {
      tier: 'tier_3',
      single: ngn('500000000'),
      daily: ngn('1000000000'),
      monthly: ngn('5000000000'),
      updatedAt: null,
      updatedByAdminId: null,
    },
  ];
}

/** A sample card image (an inline SVG, base64) for a redemption that shipped a photo. */
const SAMPLE_CARD_IMAGE =
  'PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSczMjAnIGhlaWdodD0nMjAwJz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9J2cnIHgxPScwJyB5MT0nMCcgeDI9JzEnIHkyPScxJz48c3RvcCBvZmZzZXQ9JzAnIHN0b3AtY29sb3I9JyM1QjJDRTYnLz48c3RvcCBvZmZzZXQ9JzEnIHN0b3AtY29sb3I9JyMxNkM3ODQnLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48cmVjdCB3aWR0aD0nMzIwJyBoZWlnaHQ9JzIwMCcgcng9JzE4JyBmaWxsPSd1cmwoI2cpJy8+PHRleHQgeD0nMjYnIHk9JzYwJyBmaWxsPSd3aGl0ZScgZm9udC1mYW1pbHk9J0FyaWFsJyBmb250LXNpemU9JzIyJyBmb250LXdlaWdodD0nYm9sZCc+R0VORVggR0lGVDwvdGV4dD48dGV4dCB4PScyNicgeT0nMTUwJyBmaWxsPSd3aGl0ZScgZm9udC1mYW1pbHk9J21vbm9zcGFjZScgZm9udC1zaXplPScyNicgbGV0dGVyLXNwYWNpbmc9JzMnPjQ5MjEgODgzMDwvdGV4dD48L3N2Zz4=';

/**
 * Pending redemptions the reviewer works. Stored as the *detail* shape (code + image and
 * all) so the mock's item route can serve the reviewer-only fields; the queue route
 * projects each down to its queue-item columns.
 */
export function seedGiftcardRedemptions(): AdminRedemptionDetail[] {
  const brands = seedGiftcardBrands();
  const rows: Array<{
    brandIdx: number;
    faceDollars: number;
    quantity: number;
    code: string;
    withImage: boolean;
    phoneSeed: number;
    daysAgo: number;
  }> = [
    { brandIdx: 0, faceDollars: 50, quantity: 1, code: 'AMZN-5X7K-9QW2-4LP8', withImage: true, phoneSeed: 17, daysAgo: 0 },
    { brandIdx: 2, faceDollars: 100, quantity: 1, code: 'STEAM-4K2H-88MN-QZ10', withImage: false, phoneSeed: 41, daysAgo: 0 },
    { brandIdx: 1, faceDollars: 25, quantity: 2, code: 'ITUN-9930-1120-7742', withImage: false, phoneSeed: 63, daysAgo: 1 },
    { brandIdx: 3, faceDollars: 200, quantity: 1, code: 'GPLAY-7712-3390-0021', withImage: true, phoneSeed: 88, daysAgo: 1 },
    { brandIdx: 0, faceDollars: 15, quantity: 3, code: 'AMZN-2210-6654-9081', withImage: false, phoneSeed: 12, daysAgo: 2 },
  ];
  return rows.map((r, i) => {
    const brand = brands[r.brandIdx]!;
    const nairaPerUnit = Number(brand.rateNairaPerUnit.numerator) / Number(brand.rateNairaPerUnit.denominator); // kobo
    const payoutKobo = BigInt(Math.round(nairaPerUnit)) * BigInt(r.faceDollars) * BigInt(r.quantity);
    return {
      id: `gr000000-0000-4000-8000-${String(600000 + i).padStart(12, '0')}`,
      maskedPhone: maskPhone(r.phoneSeed),
      // The full number too, for the same reason every other operator read model carries one:
      // a reviewer holding a redemption they cannot approve has to be able to ring the person.
      ...phoneFields(r.phoneSeed),
      brandName: brand.name,
      faceValue: usd(r.faceDollars),
      quantity: r.quantity,
      payoutAmount: { currency: 'NGN', amount: payoutKobo.toString() },
      status: 'pending',
      createdAt: iso(r.daysAgo, 9 + i),
      userId: `u0000000-0000-4000-8000-${String(100001 + i).padStart(12, '0')}`,
      faceCurrency: 'USD',
      cardCode: r.code,
      cardCodes: [r.code],
      image: r.withImage ? SAMPLE_CARD_IMAGE : null,
      imageMime: r.withImage ? 'image/svg+xml' : null,
      reason: null,
      reviewedAt: null,
      reviewedByAdminId: null,
    };
  });
}

// ── Support (chat + tickets) ─────────────────────────────────────────────────────

const SUPERADMIN_ID = MOCK_ADMINS[0]!.id;
const SUPPORT_ID = MOCK_ADMINS[3]!.id;

/** A message as it lives in the seed — `mine` is derived per-viewer by the transport. */
export type MockMessage = Omit<Message, 'mine'>;

/** A thread plus its full message log; the inbox route projects the row, thread route serves messages. */
export interface MockSupportThread extends AdminThreadRow {
  messages: MockMessage[];
}

/** A precise timestamp helper for ordering messages within a thread. */
const ts = (daysAgo: number, hour: number, min: number): string =>
  new Date(Date.UTC(2026, 6, 30 - daysAgo, hour, min, 0)).toISOString();

/** A tiny receipt file (text/plain, base64) attached to one thread. */
const RECEIPT_FILE =
  'R2VuRXggUGF5IOKAlCBwYXltZW50IHJlY2VpcHQKUmVmZXJlbmNlOiBHWC04SDJLOTEKQW1vdW50OiBOR04gMjUsMDAwLjAwClN0YXR1czogc2V0dGxlZApEYXRlOiAyMDI2LTA3LTI5Cg==';

/**
 * The attachment byte store the mock media route serves. Keyed by attachment id, holding
 * the base64 payload exactly as the auth-scoped `/admin/support/media/:id` route returns
 * it — an image the thread view renders inline, and a file it offers for download.
 */
export const SUPPORT_MEDIA: Readonly<Record<string, { mime: string; name: string; dataBase64: string }>> = {
  'at000000-0000-4000-8000-000000000001': {
    mime: 'image/svg+xml',
    name: 'screenshot.svg',
    dataBase64: SAMPLE_CARD_IMAGE,
  },
  'at000000-0000-4000-8000-000000000002': {
    mime: 'text/plain',
    name: 'receipt.txt',
    dataBase64: RECEIPT_FILE,
  },
};

function attach(id: string): Attachment {
  const m = SUPPORT_MEDIA[id]!;
  // Decoded byte size — base64 without padding, 3 bytes per 4 chars.
  const clean = m.dataBase64.replace(/=+$/, '');
  const size = Math.floor((clean.length * 3) / 4);
  return { id, mime: m.mime, size, name: m.name };
}

function msg(
  threadId: string,
  seq: number,
  senderType: SenderType,
  body: string | null,
  createdAt: string,
  attachments: Attachment[] = [],
): MockMessage {
  return {
    id: `ms000000-0000-4000-8000-${String(700000 + seq).padStart(12, '0')}`,
    threadId,
    senderType,
    body,
    attachments,
    createdAt,
  };
}

export function seedSupportThreads(): MockSupportThread[] {
  const T1 = 'th000000-0000-4000-8000-000000000001';
  const T2 = 'th000000-0000-4000-8000-000000000002';
  const T3 = 'th000000-0000-4000-8000-000000000003';
  const T4 = 'th000000-0000-4000-8000-000000000004';
  const T5 = 'th000000-0000-4000-8000-000000000005';
  const T6 = 'th000000-0000-4000-8000-000000000006';

  return [
    {
      id: T1,
      userId: 'u0000000-0000-4000-8000-000000100002',
      maskedPhone: maskPhone(21),
      ...phoneFields(21),
      displayName: 'Ngozi Adeyemi',
      type: 'chat',
      subject: 'My crypto sell has not settled',
      status: 'open',
      priority: 'high',
      unreadForAdmin: 2,
      assignedAdminId: null,
      lastMessageAt: ts(0, 8, 42),
      createdAt: ts(0, 8, 30),
      messages: [
        msg(T1, 1, 'customer', 'Hi, I sold 0.01 BTC about 30 minutes ago and my naira balance still has not updated. Order ref GX-8H2K91.', ts(0, 8, 30)),
        msg(T1, 2, 'system', 'Thanks for reaching out. An agent will be with you shortly. Our hours are 8:00 to 20:00 WAT.', ts(0, 8, 30)),
        msg(T1, 3, 'customer', 'Here is the receipt I got in the app.', ts(0, 8, 42), [attach('at000000-0000-4000-8000-000000000002')]),
      ],
    },
    {
      id: T2,
      userId: 'u0000000-0000-4000-8000-000000100005',
      maskedPhone: maskPhone(47),
      ...phoneFields(47),
      displayName: 'Kunle Okonkwo',
      type: 'ticket',
      subject: 'Wrong bank account on a transfer',
      status: 'pending',
      priority: 'urgent',
      unreadForAdmin: 1,
      assignedAdminId: SUPPORT_ID,
      lastMessageAt: ts(0, 10, 15),
      createdAt: ts(1, 16, 5),
      messages: [
        msg(T2, 10, 'customer', 'I sent ₦40,000 to the wrong account number. Can this be reversed?', ts(1, 16, 5)),
        msg(T2, 11, 'admin', 'I understand. Let me check the payout status. Do you have the recipient account you intended?', ts(1, 16, 40)),
        msg(T2, 12, 'customer', 'Yes it should have been 0123456789 GTBank. I attached a screenshot.', ts(0, 10, 15), [attach('at000000-0000-4000-8000-000000000001')]),
      ],
    },
    {
      id: T3,
      userId: 'u0000000-0000-4000-8000-000000100008',
      maskedPhone: maskPhone(63),
      ...phoneFields(63),
      displayName: null,
      type: 'chat',
      subject: 'Cannot complete tier 2 verification',
      status: 'open',
      priority: 'normal',
      unreadForAdmin: 0,
      assignedAdminId: SUPPORT_ID,
      lastMessageAt: ts(1, 9, 20),
      createdAt: ts(2, 11, 0),
      messages: [
        msg(T3, 20, 'customer', 'My BVN keeps getting rejected during verification. The details match my bank exactly.', ts(2, 11, 0)),
        msg(T3, 21, 'admin', 'Sorry about that. Sometimes the name order differs. Could you confirm your date of birth so I can check the record?', ts(2, 11, 30)),
        msg(T3, 22, 'customer', 'Sent it in the app already. Please check.', ts(1, 9, 20)),
      ],
    },
    {
      id: T4,
      userId: 'u0000000-0000-4000-8000-000000100011',
      maskedPhone: maskPhone(88),
      ...phoneFields(88),
      displayName: 'Bola Nwosu',
      type: 'chat',
      subject: 'How do I convert NGN to USD?',
      status: 'resolved',
      priority: 'low',
      unreadForAdmin: 0,
      assignedAdminId: SUPPORT_ID,
      lastMessageAt: ts(2, 14, 10),
      createdAt: ts(3, 13, 0),
      messages: [
        msg(T4, 30, 'customer', 'Is it possible to hold USD in the app now? How do I convert?', ts(3, 13, 0)),
        msg(T4, 31, 'admin', 'Yes! Open Convert, pick NGN → USD, and confirm the quote. Your USD balance shows on the home screen.', ts(3, 13, 20)),
        msg(T4, 32, 'customer', 'Worked perfectly, thank you!', ts(2, 14, 10)),
        msg(T4, 33, 'system', 'This conversation was marked resolved.', ts(2, 14, 11)),
      ],
    },
    {
      id: T5,
      userId: 'u0000000-0000-4000-8000-000000100014',
      maskedPhone: maskPhone(12),
      ...phoneFields(12),
      displayName: 'Aisha Bello',
      type: 'ticket',
      subject: 'Airtime top-up failed but I was charged',
      status: 'open',
      priority: 'high',
      unreadForAdmin: 1,
      assignedAdminId: null,
      lastMessageAt: ts(0, 7, 5),
      createdAt: ts(0, 7, 5),
      messages: [
        msg(T5, 40, 'customer', 'I bought ₦1,000 airtime, the app said failed but ₦1,000 left my wallet. Please refund.', ts(0, 7, 5)),
        msg(T5, 41, 'system', 'Thanks for reaching out. An agent will be with you shortly.', ts(0, 7, 5)),
      ],
    },
    {
      id: T6,
      userId: 'u0000000-0000-4000-8000-000000100003',
      maskedPhone: maskPhone(35),
      ...phoneFields(35),
      displayName: 'Tunde Balogun',
      type: 'chat',
      subject: 'Feature request: recurring transfers',
      status: 'closed',
      priority: 'low',
      unreadForAdmin: 0,
      assignedAdminId: SUPERADMIN_ID,
      lastMessageAt: ts(5, 12, 0),
      createdAt: ts(6, 10, 0),
      messages: [
        msg(T6, 50, 'customer', 'It would be great to schedule a monthly transfer to my landlord.', ts(6, 10, 0)),
        msg(T6, 51, 'admin', 'Great idea. I have logged it for the product team. Thank you for the suggestion!', ts(6, 10, 30)),
        msg(T6, 52, 'system', 'This conversation was closed.', ts(5, 12, 0)),
      ],
    },
  ];
}

export function seedSupportSettings(): SupportSettings {
  return {
    supportEmail: 'support@genex.pay',
    autoReplyEnabled: true,
    autoReplyMessage:
      'Thanks for reaching out. An agent will be with you shortly. Our hours are 8:00 to 20:00 WAT, Monday to Saturday.',
    businessHours: {
      timezone: 'Africa/Lagos',
      days: [
        { day: 'mon', open: '08:00', close: '20:00' },
        { day: 'tue', open: '08:00', close: '20:00' },
        { day: 'wed', open: '08:00', close: '20:00' },
        { day: 'thu', open: '08:00', close: '20:00' },
        { day: 'fri', open: '08:00', close: '20:00' },
        { day: 'sat', open: '10:00', close: '16:00' },
      ],
    },
  };
}

/**
 * The three legal documents, as the seeded API ships them: `{{platformName}}` tokens intact
 * (the console edits the source, the API substitutes on the public read) and short enough to
 * keep the demo bundle small. The real starter documents live in the API's seed.
 */
export function seedLegalDocuments(): AdminLegalDocument[] {
  return [
    {
      key: 'about',
      title: 'About {{platformName}}',
      body: [
        '# About {{platformName}}',
        '',
        '{{platformName}} turns crypto into naira, and naira into everything else you need to pay for.',
        '',
        '## Talk to us',
        '',
        'Help and live chat in the app reaches a real person. You can also write to {{supportEmail}}.',
      ].join('\n'),
      updatedByAdminId: null,
      updatedAt: iso(9),
    },
    {
      key: 'privacy',
      title: 'Privacy Policy',
      body: [
        '# Privacy Policy',
        '',
        'This policy explains what {{platformName}} does with your personal information.',
        '',
        '## What we collect',
        '',
        '- Who you are, and the identity details you give us at verification.',
        '- How you use the app: your transactions, balances and saved beneficiaries.',
        '',
        '## Your rights',
        '',
        'Write to {{supportEmail}} to see, correct or delete what we hold about you.',
      ].join('\n'),
      updatedByAdminId: 'a1000000-0000-4000-8000-000000000001',
      updatedAt: iso(6),
    },
    {
      key: 'terms',
      title: 'Terms of Service',
      body: [
        '# Terms of Service',
        '',
        'These terms are the agreement between you and {{platformName}} for the use of our app.',
        '',
        '## 1. Your account',
        '',
        '1. You must be at least 18 and using the app for yourself.',
        '2. You are responsible for everything done through your account.',
        '',
        '## 2. Buying and selling crypto',
        '',
        '1. Every trade is priced by a quote we show you before you confirm.',
        '2. Once you confirm a trade it is final.',
        '',
        '## 3. Contact',
        '',
        'Write to {{supportEmail}}, or start a chat from Help and live chat in the app.',
      ].join('\n'),
      updatedByAdminId: 'a1000000-0000-4000-8000-000000000001',
      updatedAt: iso(6),
    },
  ];
}
// ── Deposit rails ───────────────────────────────────────────────────────────────

/**
 * The eight (currency, network) rails, mirroring `ASSET_NETWORKS` in `@genex/contracts`.
 *
 * Copied rather than imported because the console keeps no *runtime* dependency on the
 * contracts package — every other type here is a type-only re-export, and pulling in a
 * value would drag zod and `@genex/money` into the bundle. The copy is only ever read by
 * the offline demo, so drifting from the real catalogue costs a slightly stale mock and
 * nothing an operator could act on.
 */
const MOCK_ASSET_CATALOGUE: readonly AssetNetwork[] = [
  {
    currency: 'BTC',
    network: 'Bitcoin',
    short: 'Bitcoin',
    custodyTicker: 'btc',
    confirmationsRequired: 2,
    minDeposit: '0.0001',
    minWithdrawal: '0.0002',
    placeholder: 'bc1… or 1… / 3…',
    hint: 'A Bitcoin mainnet address: bech32 (bc1…) or legacy (1… / 3…).',
  },
  {
    currency: 'ETH',
    network: 'Ethereum (ERC-20)',
    short: 'ERC-20',
    custodyTicker: 'eth',
    confirmationsRequired: 12,
    minDeposit: '0.005',
    minWithdrawal: '0.005',
    placeholder: '0x…',
    hint: 'An Ethereum address: 0x followed by 40 hex characters.',
  },
  {
    currency: 'SOL',
    network: 'Solana',
    short: 'Solana',
    custodyTicker: 'sol',
    confirmationsRequired: 32,
    minDeposit: '0.05',
    minWithdrawal: '0.05',
    placeholder: 'Base58 address',
    hint: 'A Solana address: a base58 string, 32 to 44 characters.',
  },
  {
    currency: 'USDT',
    network: 'Tron (TRC-20)',
    short: 'TRC-20',
    custodyTicker: 'usdttrc20',
    confirmationsRequired: 20,
    minDeposit: '1',
    minWithdrawal: '10',
    placeholder: 'T…',
    hint: 'A Tron (TRC-20) address: starts with T, 34 characters.',
  },
  {
    currency: 'USDT',
    network: 'Ethereum (ERC-20)',
    short: 'ERC-20',
    custodyTicker: 'usdterc20',
    confirmationsRequired: 12,
    minDeposit: '1',
    minWithdrawal: '20',
    placeholder: '0x…',
    hint: 'An Ethereum (ERC-20) address: 0x followed by 40 hex characters.',
  },
  {
    currency: 'USDT',
    network: 'Solana (SPL)',
    short: 'SOL',
    custodyTicker: 'usdtsol',
    confirmationsRequired: 32,
    minDeposit: '1',
    minWithdrawal: '5',
    placeholder: 'Base58 address',
    hint: 'A Solana (SPL) address: a base58 string, 32 to 44 characters.',
  },
  {
    currency: 'USDC',
    network: 'Ethereum (ERC-20)',
    short: 'ERC-20',
    custodyTicker: 'usdc',
    confirmationsRequired: 12,
    minDeposit: '1',
    minWithdrawal: '20',
    placeholder: '0x…',
    hint: 'An Ethereum (ERC-20) address: 0x followed by 40 hex characters.',
  },
  {
    currency: 'USDC',
    network: 'Solana (SPL)',
    short: 'SOL',
    custodyTicker: 'usdcsol',
    confirmationsRequired: 32,
    minDeposit: '1',
    minWithdrawal: '5',
    placeholder: 'Base58 address',
    hint: 'A Solana (SPL) address: a base58 string, 32 to 44 characters.',
  },
];

/**
 * The rails as the console reads them, with one rail deliberately overridden.
 *
 * USDT on Ethereum carries an operator minimum well above the catalogue's, which is the
 * realistic case — ERC-20 gas makes a one-dollar deposit cost more to sweep than it is
 * worth — and it means the demo shows the "operator" provenance chip rather than eight
 * identical rows that make the page look read-only.
 */
export function seedAssetRails(): AdminAssetRail[] {
  return MOCK_ASSET_CATALOGUE.map((rail) => {
    const overridden = rail.currency === 'USDT' && rail.network === 'Ethereum (ERC-20)';
    return {
      rail,
      custodianMinDeposit: null,
      overrideMinDeposit: overridden ? '25' : null,
      overrideConfirmations: null,
      effectiveMinDeposit: overridden ? '25' : rail.minDeposit,
      minDepositSource: overridden ? ('operator' as const) : ('catalogue' as const),
      effectiveConfirmations: rail.confirmationsRequired,
      confirmationsSource: 'catalogue' as const,
      updatedByAdminId: overridden ? (MOCK_ADMINS[1]?.id ?? null) : null,
      updatedAt: overridden ? iso(6, 11) : null,
    };
  });
}

// ── Bill services ──────────────────────────────────────────────────────────────────

/**
 * The uploaded icons, keyed by service id.
 *
 * Two of the seeded services carry one and five do not, which is the state a real deployment
 * starts in: the catalogue ships with letter marks on hex tiles and an operator replaces them
 * one at a time. A demo where every tile already had artwork would never show the lettermark
 * the app actually renders today, and would make the upload control look decorative.
 *
 * Mutable, because uploading in the demo has to stick for the tab session exactly as every
 * other write in this mock does.
 */
export const BILL_ICONS: Record<string, { mime: string; name: string; dataBase64: string }> = {
  'bs000000-0000-4000-8000-000000000001': {
    mime: 'image/png',
    name: 'ikeja-electric.png',
    dataBase64: DEMO_ICON_BOLT,
  },
  'bs000000-0000-4000-8000-000000000004': {
    mime: 'image/png',
    name: 'dstv.png',
    dataBase64: DEMO_ICON_TV,
  },
};

/**
 * The bill catalogue, as the customer app is served it plus the operator's icon.
 *
 * Seeded to look like the real Nigerian biller set — an electricity disco, three telcos, two
 * pay-TV operators and a betting wallet — because the page's whole job is judging how a tile
 * reads, and lorem-ipsum marks on random colours cannot be judged. One service is switched
 * off, so the "unavailable" state is visible without an operator having to break something to
 * see it.
 */
export function seedBillServices(): BillService[] {
  const naira = (n: number) => ({ currency: 'NGN' as const, amount: String(n * 100) });
  const rows: ReadonlyArray<{
    id: string;
    mark: string;
    name: string;
    color: string;
    identifierKind: BillService['identifierKind'];
    identifierLabel: string;
    presets: readonly number[];
    min: number;
    max: number;
    available: boolean;
  }> = [
    { id: 'bs000000-0000-4000-8000-000000000001', mark: 'IE', name: 'Ikeja Electric', color: '#0E74C8', identifierKind: 'meter', identifierLabel: 'Meter number', presets: [1000, 2000, 5000, 10000], min: 500, max: 500000, available: true },
    { id: 'bs000000-0000-4000-8000-000000000002', mark: 'MTN', name: 'MTN Airtime', color: '#FFC403', identifierKind: 'phone', identifierLabel: 'Phone number', presets: [100, 200, 500, 1000], min: 50, max: 50000, available: true },
    { id: 'bs000000-0000-4000-8000-000000000003', mark: 'GLO', name: 'Glo Data', color: '#4CAF50', identifierKind: 'phone', identifierLabel: 'Phone number', presets: [500, 1000, 2000, 5000], min: 100, max: 50000, available: true },
    { id: 'bs000000-0000-4000-8000-000000000004', mark: 'DStv', name: 'DStv', color: '#161E3C', identifierKind: 'smartcard', identifierLabel: 'Smartcard number', presets: [4400, 8000, 15700, 24500], min: 1000, max: 200000, available: true },
    { id: 'bs000000-0000-4000-8000-000000000005', mark: 'GOtv', name: 'GOtv', color: '#00A94F', identifierKind: 'smartcard', identifierLabel: 'IUC number', presets: [1900, 3300, 4850, 7200], min: 900, max: 100000, available: false },
    { id: 'bs000000-0000-4000-8000-000000000006', mark: 'AIR', name: 'Airtel Airtime', color: '#E4002B', identifierKind: 'phone', identifierLabel: 'Phone number', presets: [100, 200, 500, 1000], min: 50, max: 50000, available: true },
    { id: 'bs000000-0000-4000-8000-000000000007', mark: 'BET', name: 'Bet9ja Wallet', color: '#1B7A3E', identifierKind: 'account', identifierLabel: 'Account ID', presets: [500, 1000, 2500, 5000], min: 100, max: 100000, available: true },
  ];

  return rows.map((row) => {
    const icon = BILL_ICONS[row.id];
    return {
      id: row.id,
      mark: row.mark,
      name: row.name,
      color: row.color,
      identifierKind: row.identifierKind,
      identifierLabel: row.identifierLabel,
      presets: row.presets.map(naira),
      minAmount: naira(row.min),
      maxAmount: naira(row.max),
      available: row.available,
      /*
       * The public serving path, with a cache-busting stamp. Null for a service that has never
       * had an icon uploaded, which is the shipped state for every one of them and the reason
       * the letter mark and the colour behind it stay editable rather than being retired.
       */
      iconPath: icon === undefined ? null : `/v1/bills/services/${row.id}/icon?v=1787136339000`,
    };
  });
}
